import * as React from "react";
import { toast } from "react-toastify";
import { gettext } from "i18n";
import {
  faMinimize,
  faArrowsLeftRight,
  faMaximize,
  faExpand,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { VisualSettings } from "./widgets";

/**
 * Shared dashboard layout and widget sizing utilities
 */

export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = React.useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  React.useEffect(() => {
    const media = window.matchMedia(query);
    const updateMatch = () => setMatches(media.matches);
    updateMatch();
    media.addEventListener("change", updateMatch);
    return () => media.removeEventListener("change", updateMatch);
  }, [query]);

  return matches;
};

export interface SizePreset {
  key: string;
  label: string;
  colSpan: number;
  icon: IconDefinition;
}

export const SIZE_PRESETS: SizePreset[] = [
  { key: "narrow", label: gettext("Narrow"), colSpan: 3, icon: faMinimize },
  {
    key: "medium",
    label: gettext("Medium"),
    colSpan: 6,
    icon: faArrowsLeftRight,
  },
  { key: "wide", label: gettext("Wide"), colSpan: 9, icon: faMaximize },
  { key: "full", label: gettext("Full"), colSpan: 12, icon: faExpand },
];

export const DEFAULT_COL_SPAN = 6;

export const getSizePreset = (colSpan: number | undefined): SizePreset => {
  return (
    SIZE_PRESETS.find((preset) => preset.colSpan === colSpan) ?? SIZE_PRESETS[1]
  );
};

export interface WidgetWithColSpan {
  colSpan?: number;
}

export const calculateWidgetWidthDuringDrag = <T extends WidgetWithColSpan>(
  widget: T,
  allWidgets: T[],
  defaultColSpan: number = DEFAULT_COL_SPAN,
): string => {
  const originalColSpan = widget.colSpan ?? defaultColSpan;
  const numWidgets = allWidgets.length;

  if (numWidgets <= 1) {
    return `${(originalColSpan / 12) * 100}%`;
  }

  const totalOriginalColSpan = allWidgets.reduce(
    (sum, w) => sum + (w.colSpan ?? defaultColSpan),
    0,
  );

  if (totalOriginalColSpan <= 0) {
    return `${(originalColSpan / 12) * 100}%`;
  }

  const numDropZones = numWidgets + 1;
  const dropZoneWidth = 40;
  const totalDropZoneWidth = numDropZones * dropZoneWidth;
  const gridProportion = originalColSpan / 12;

  return `calc((100% - ${totalDropZoneWidth}px) * ${gridProportion})`;
};

export interface WidgetStructure {
  key: string;
  metaData: Record<string, any>;
  dateRange?: [string, string];
  visualSettings?: VisualSettings;
  colSpan?: number;
  minColSpan?: number | null;
  maxColSpan?: number | null;
}

export interface WidgetRowStructure {
  widgets: WidgetStructure[];
}

export interface StationStructure {
  identifier: string;
  widget_rows: WidgetRowStructure[];
}

export interface DragPayload<T> {
  widget: T;
  widgetIdx: number;
  widgetGroupIdx: number;
  scope?: string;
}

export type DropPosition =
  | {
      type: "widget";
      rowIndex: number;
      widgetIndex: number;
      side: "left" | "right";
      scope?: string;
    }
  | { type: "row"; rowIndex: number; isNewRow?: boolean; scope?: string };

export interface WidgetDropResult<T> {
  newWidgetRows: T[];
  hasChanges: boolean;
}

export function calculateWidgetDrop<T extends { widgets: unknown[] }>(
  widgetRows: T[],
  dragPayload: DragPayload<unknown>,
  position: DropPosition,
): WidgetDropResult<T> {
  const {
    widget,
    widgetIdx: oldWidgetIdx,
    widgetGroupIdx: oldWidgetGroupIdx,
  } = dragPayload;

  let targetRowIndex: number;
  let targetWidgetIndex: number;
  let isNewRow = false;

  if (position.type === "row") {
    targetRowIndex = position.rowIndex;
    targetWidgetIndex = 0;
    isNewRow = position.isNewRow ?? true;
  } else {
    targetRowIndex = position.rowIndex;
    targetWidgetIndex =
      position.side === "left"
        ? (position.widgetIndex ?? 0)
        : (position.widgetIndex ?? 0) + 1;
  }

  const isSameRow = oldWidgetGroupIdx === targetRowIndex;
  const droppingOnOwnLeftZone =
    isSameRow &&
    position.type === "widget" &&
    position.side === "left" &&
    position.widgetIndex === oldWidgetIdx;

  if (droppingOnOwnLeftZone) {
    return { newWidgetRows: widgetRows, hasChanges: false };
  }

  // The dragged widget must exist at its source position, and an existing
  // target row must exist, otherwise the move would duplicate the widget or
  // remove an unrelated one.
  const sourceWidgets = widgetRows[oldWidgetGroupIdx]?.widgets;
  if (
    !sourceWidgets ||
    oldWidgetIdx < 0 ||
    oldWidgetIdx >= sourceWidgets.length ||
    (!isNewRow && !widgetRows[targetRowIndex])
  ) {
    return { newWidgetRows: widgetRows, hasChanges: false };
  }

  let newStructure = [...widgetRows];

  if (isNewRow) {
    const newRow = {
      widgets: [widget],
    } as unknown as T;
    newStructure.splice(targetRowIndex, 0, newRow);

    const adjustedOldGroupIdx =
      oldWidgetGroupIdx >= targetRowIndex
        ? oldWidgetGroupIdx + 1
        : oldWidgetGroupIdx;

    newStructure[adjustedOldGroupIdx] = {
      ...newStructure[adjustedOldGroupIdx],
      widgets: newStructure[adjustedOldGroupIdx].widgets.filter(
        (_: unknown, idx: number) => idx !== oldWidgetIdx,
      ),
    } as T;
  } else {
    const targetRowWidgets = widgetRows[targetRowIndex]?.widgets ?? [];
    const newTargetWidgets = [...targetRowWidgets];
    newTargetWidgets.splice(targetWidgetIndex, 0, widget);

    newStructure[targetRowIndex] = {
      ...newStructure[targetRowIndex],
      widgets: newTargetWidgets,
    } as T;

    if (oldWidgetGroupIdx === targetRowIndex) {
      const adjustedOldIndex =
        targetWidgetIndex <= oldWidgetIdx ? oldWidgetIdx + 1 : oldWidgetIdx;
      newStructure[oldWidgetGroupIdx] = {
        ...newStructure[oldWidgetGroupIdx],
        widgets: newStructure[oldWidgetGroupIdx].widgets.filter(
          (_: unknown, idx: number) => idx !== adjustedOldIndex,
        ),
      } as T;
    } else {
      newStructure[oldWidgetGroupIdx] = {
        ...newStructure[oldWidgetGroupIdx],
        widgets: newStructure[oldWidgetGroupIdx].widgets.filter(
          (_: unknown, idx: number) => idx !== oldWidgetIdx,
        ),
      } as T;
    }
  }

  newStructure = newStructure.filter(
    (row: T, idx: number) => row.widgets?.length > 0 || idx === targetRowIndex,
  );

  return { newWidgetRows: newStructure, hasChanges: true };
}

/**
 * Returns a copy of a dashboard structure with a single widget replaced by
 * the result of `updater`. The original structure and widget objects are not
 * mutated, so the saved dashboard structure stays intact until saved. If no
 * widget exists at the given position, the original structure is returned.
 */
export function updateWidgetInStructure<S extends { station: any }>(
  structure: S[],
  stationIndex: number,
  rowIndex: number,
  widgetIndex: number,
  updater: (widget: any) => any,
): S[] {
  const stationItem = structure[stationIndex];
  const row = stationItem?.station?.widget_rows?.[rowIndex];
  if (!row?.widgets || widgetIndex < 0 || widgetIndex >= row.widgets.length) {
    return structure;
  }

  const newRow = {
    ...row,
    widgets: row.widgets.map((widget: any, wIdx: number) =>
      wIdx !== widgetIndex ? widget : updater(widget),
    ),
  };
  const newStationItem = {
    ...stationItem,
    station: {
      ...stationItem.station,
      widget_rows: stationItem.station.widget_rows.map(
        (existingRow: any, rIdx: number) =>
          rIdx !== rowIndex ? existingRow : newRow,
      ),
    },
  };
  return structure.map((item, sIdx) =>
    sIdx !== stationIndex ? item : newStationItem,
  );
}

/**
 * Builds the widget saved from the widget edit form: the form's key and
 * metadata replace the old ones, while layout and display settings that the
 * form does not edit (colSpan, visualSettings, dateRange…) are kept.
 */
export function mergeEditedWidget(
  existingWidget: Record<string, any> | undefined,
  key: string,
  metaData: Record<string, any>,
): Record<string, any> {
  return {
    ...(existingWidget ?? {}),
    key,
    metaData,
  };
}

/**
 * Copying reads the widget from the saved dashboard by its position, and
 * copying to the same dashboard reloads it, so unsaved edits would make it
 * copy the wrong widget or discard the edits. Returns true when copying may
 * proceed, otherwise tells the user to save first.
 */
export function canCopyWidget(changesMade: boolean): boolean {
  if (changesMade) {
    toast.warning(
      gettext("Save or cancel your changes before copying a widget."),
    );
    return false;
  }
  return true;
}

/**
 * Pairs a row's stored widgets with their widget type definitions, keeping
 * each widget's index in the stored row. Widgets of unknown type are dropped,
 * or represented by `unknownPlaceholder` when it is given (while editing, so
 * that indices have no gaps and the widget can be removed).
 */
export function resolveRowWidgets<
  W extends { key: string },
  D extends { key: string },
>(
  widgets: W[],
  definitions: D[],
  unknownPlaceholder?: (key: string) => D,
): { definition: D; widgetParams: W; widgetIdx: number }[] {
  return widgets.flatMap((widgetParams, widgetIdx) => {
    const definition =
      definitions.find((d) => d.key === widgetParams.key) ??
      unknownPlaceholder?.(widgetParams.key);
    return definition ? [{ definition, widgetParams, widgetIdx }] : [];
  });
}
